import CSREntry from 'src/clientRoutes/landing.welcome._index.csr'
import FullSSREntry from 'src/clientRoutes/landing.welcome._index.full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/landing.welcome._index'

export const handle = {
  name: 'landing/welcome',
  track: { scene: 'landing', section: 'welcome' },
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

export async function loader({ request, context }: Route.LoaderArgs) {
  const { readPaymentItemsFromCache, prewarmPaymentItemsForSSR } =
    await import('src/utils/ssr-meta/graphql/payment.server')
  // Touch the i18n middleware context so `useTranslation('blackboard', ...)`
  // in the server-rendered sections has the right locale loaded on the
  // instance. The `blackboard` namespace is on the preload list in
  // `src/i18n/i18n.base.ts`, so `t()` on the server returns real strings.
  getI18n(context)

  const title = 'Anime AI Generator丨PixAI'
  const description =
    'Experience PixAI: your go-to anime AI generator for unique, AI-created illustrations.'
  const socialImage =
    'https://images-ng.pixai.art/images/orig/732e7ea4-fb5f-4535-acee-02c4cdd71aa9'

  const meta = staticMetaLoader({
    requestUrl: request.url,
    title,
    description,
    image: socialImage,
  })

  // Cache-only read for `paymentItems`: never block the SSR shell on a
  // network round-trip. The previous implementation `await`ed
  // `fetchPaymentItemsForSSR()` (5x retries with exponential backoff,
  // no per-attempt timeout) which on a slow/unreachable api.pixai.art
  // would routinely overshoot `entry.server.tsx`'s 6s abort window —
  // React aborted the stream, React Router re-ran the request through
  // the error boundary, and from the user's perspective the page
  // appeared to "refresh" 2-3 times before finally returning 200.
  //
  // New behaviour:
  // - Cache hit  → SSR ships real prices on the first flush, zero
  //   skeleton-to-number flicker on hydration.
  // - Cache miss → SSR ships the `PaidPlanCardFallback` skeleton (DOM
  //   shape identical to the live card, see `PricingSection.tsx`) and
  //   simultaneously kicks off a background fetch that writes the
  //   cache for subsequent requests. The client-side
  //   `useLandingPricing()` continues to fetch through Apollo and
  //   swaps the skeleton for the live card on the same render — the
  //   one transition users see is the same one they would see in the
  //   normal SPA flow, not a series of full-page reloads.
  //
  // This keeps the SSR shell flush <100ms regardless of upstream
  // payments-API latency and means the first cold visit is the
  // *only* visit that ever shows the skeleton.
  const cachedPaymentItems = readPaymentItemsFromCache()
  if (!cachedPaymentItems) {
    prewarmPaymentItemsForSSR()
  }

  return {
    ...meta,
    initialPaymentItems: cachedPaymentItems ?? undefined,
  }
}

export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

/**
 * Opt out of a `HydrateFallback`. The landing route renders real page
 * components both on the server and the client, so the framework never has
 * to show a shell between server render and hydration.
 */
export function HydrateFallback() {
  return null
}

/**
 * Dual-mode entry. `'off'` (CSR) path renders `<LandingHome />`
 * directly under `<RootRuntime>`'s global Apollo / QueryClient with
 * no SSR pricing seed; `'on'` (Full SSR) path mounts the
 * route-scoped Apollo client + `initialPaymentItems` bridge from
 * `clientRoutes/landing.welcome._index.full-ssr.tsx`.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
